import React, { useState, useEffect } from "react";
import {
  assignPathwayToGroupAsync,
  removePathwayFromGroupAsync,
  assignCourseToGroupAsync,
  removeCourseFromGroupAsync,
  getLearningPathwaysAsync,
  populateCourseDropdownAsync,
} from "../../../Services/CourseService";
import { showToast } from "../../../Infrastructure/Notifications/ToastrConfig";
import Modal from "../../../Shared/Components/Modal/Modal";
import { ADMIN, userIsInRole } from "../../../Services/AuthService";

const GroupAssignments = ({
  groupId,
  assignedCourses = [],
  assignedPathways = [],
  onUpdate,
}) => {
  const isAdmin = userIsInRole(ADMIN);
  const [activeTab, setActiveTab] = useState("pathways");
  const [allPathways, setAllPathways] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [selectedPathwayId, setSelectedPathwayId] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [itemToRemove, setItemToRemove] = useState({});

  useEffect(() => {
    fetchData();
  }, [groupId]);

  const fetchData = async () => {
    try {
      // Fetch all available pathways
      const pathwaysResult = await getLearningPathwaysAsync();
      setAllPathways(pathwaysResult.learningPathways || []);

      // Fetch all available courses
      const coursesResult = await populateCourseDropdownAsync();
      setAllCourses(coursesResult.courses || []);

      // TODO: Fetch assigned pathways and courses for this group
      // For now, we'll manage this in parent component or add new API endpoints
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleAssignPathway = async () => {
    if (!selectedPathwayId) {
      showToast(false, "Please select a learning pathway");
      return;
    }

    try {
      const response = await assignPathwayToGroupAsync({
        groupId: groupId,
        learningPathwayId: selectedPathwayId,
      });

      if (response.success) {
        showToast(true, "Learning pathway assigned to group successfully");
        setSelectedPathwayId("");
        if (onUpdate) onUpdate();
      } else {
        showToast(
          false,
          response.errors?.[0]?.error || "Failed to assign pathway",
        );
      }
    } catch (error) {
      console.error("Error assigning pathway:", error);
      showToast(false, "Failed to assign pathway");
    }
  };

  const handleAssignCourse = async () => {
    if (!selectedCourseId) {
      showToast(false, "Please select a course");
      return;
    }

    try {
      const response = await assignCourseToGroupAsync({
        groupId: groupId,
        courseId: selectedCourseId,
      });

      if (response.success) {
        showToast(true, "Course assigned to group successfully");
        setSelectedCourseId("");
        if (onUpdate) onUpdate();
      } else {
        showToast(
          false,
          response.errors?.[0]?.error || "Failed to assign course",
        );
      }
    } catch (error) {
      console.error("Error assigning course:", error);
      showToast(false, "Failed to assign course");
    }
  };

  const handleRemovePathway = async () => {
    try {
      const response = await removePathwayFromGroupAsync({
        groupId: groupId,
        learningPathwayId: itemToRemove.id,
      });

      if (response.success) {
        showToast(true, "Learning pathway removed from group");
        if (onUpdate) onUpdate();
      } else {
        showToast(false, "Failed to remove pathway");
      }
    } catch (error) {
      console.error("Error removing pathway:", error);
      showToast(false, "Failed to remove pathway");
    }
  };

  const handleRemoveCourse = async () => {
    try {
      const response = await removeCourseFromGroupAsync({
        groupId: groupId,
        courseId: itemToRemove.id,
      });

      if (response.success) {
        showToast(true, "Course removed from group");
        if (onUpdate) onUpdate();
      } else {
        showToast(false, "Failed to remove course");
      }
    } catch (error) {
      console.error("Error removing course:", error);
      showToast(false, "Failed to remove course");
    }
  };

  return (
    <>
      {/* Remove Pathway Modal */}
      <Modal
        modalId="removeGroupPathwayModal"
        bodyText={`Are you sure you want to remove ${itemToRemove.name} from this group?`}
        buttonFunc={handleRemovePathway}
        buttonText="Remove"
        title="Remove Learning Pathway"
      />

      {/* Remove Course Modal */}
      <Modal
        modalId="removeGroupCourseModal"
        bodyText={`Are you sure you want to remove ${itemToRemove.title || itemToRemove.name} from this group?`}
        buttonFunc={handleRemoveCourse}
        buttonText="Remove"
        title="Remove Course"
      />

      <div>
        {/* Tab Navigation */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "24px",
            borderBottom: "2px solid #e5e7eb",
          }}
        >
          <button
            onClick={() => setActiveTab("pathways")}
            style={{
              padding: "12px 24px",
              fontSize: "1rem",
              fontWeight: "600",
              border: "none",
              borderBottom:
                activeTab === "pathways" ? "3px solid #667eea" : "none",
              background: "transparent",
              color: activeTab === "pathways" ? "#667eea" : "#64748b",
              cursor: "pointer",
              transition: "all 0.2s",
              marginBottom: "-2px",
            }}
          >
            🎓 Learning Pathways
          </button>
          <button
            onClick={() => setActiveTab("courses")}
            style={{
              padding: "12px 24px",
              fontSize: "1rem",
              fontWeight: "600",
              border: "none",
              borderBottom:
                activeTab === "courses" ? "3px solid #667eea" : "none",
              background: "transparent",
              color: activeTab === "courses" ? "#667eea" : "#64748b",
              cursor: "pointer",
              transition: "all 0.2s",
              marginBottom: "-2px",
            }}
          >
            📚 Individual Courses
          </button>
        </div>

        {/* Pathways Tab */}
        {activeTab === "pathways" && (
          <div>
            {/* Assign Pathway Section - Admin only */}
            {isAdmin && (
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #f6f8fb 0%, #ffffff 100%)",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "2px solid #e5e7eb",
                  marginBottom: "24px",
                }}
              >
                <label
                  style={{
                    display: "block",
                    fontSize: "0.9rem",
                    fontWeight: "600",
                    color: "#64748b",
                    marginBottom: "12px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Assign Learning Pathway
                </label>
                <div
                  style={{ display: "flex", gap: "12px", alignItems: "center" }}
                >
                  <select
                    value={selectedPathwayId}
                    onChange={(e) => setSelectedPathwayId(e.target.value)}
                    className="form-control"
                    style={{
                      flex: "1",
                      padding: "12px 16px",
                      fontSize: "1rem",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      height: "48px",
                    }}
                  >
                    <option value="">Select pathway...</option>
                    {allPathways.map((pathway) => (
                      <option key={pathway.id} value={pathway.id}>
                        {pathway.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleAssignPathway}
                    style={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      padding: "12px 24px",
                      fontSize: "1rem",
                      fontWeight: "600",
                      cursor: "pointer",
                      height: "48px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Assign
                  </button>
                </div>
              </div>
            )}

            {/* Assigned Pathways List */}
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid #e5e7eb",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ background: "#f8fafc" }}>
                  <tr>
                    <th
                      style={{
                        padding: "16px",
                        textAlign: "left",
                        borderBottom: "2px solid #e5e7eb",
                        fontWeight: "600",
                        color: "#1e293b",
                      }}
                    >
                      Pathway Name
                    </th>
                    {isAdmin && (
                      <th
                        style={{
                          padding: "16px",
                          textAlign: "right",
                          borderBottom: "2px solid #e5e7eb",
                          width: "100px",
                        }}
                      >
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {assignedPathways.length === 0 ? (
                    <tr>
                      <td
                        colSpan={isAdmin ? 2 : 1}
                        style={{
                          padding: "40px",
                          textAlign: "center",
                          color: "#64748b",
                        }}
                      >
                        No learning pathways assigned to this group yet
                      </td>
                    </tr>
                  ) : (
                    assignedPathways.map((pathway) => (
                      <tr
                        key={pathway.id}
                        style={{ borderBottom: "1px solid #f1f5f9" }}
                      >
                        <td style={{ padding: "16px", color: "#1e293b" }}>
                          {pathway.name}
                        </td>
                        {isAdmin && (
                          <td style={{ padding: "16px", textAlign: "right" }}>
                            <button
                              data-target="#removeGroupPathwayModal"
                              data-toggle="modal"
                              onClick={() => setItemToRemove(pathway)}
                              style={{
                                background:
                                  "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                padding: "6px 14px",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                                cursor: "pointer",
                              }}
                            >
                              Remove
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Courses Tab */}
        {activeTab === "courses" && (
          <div>
            {/* Assign Course Section - Admin only */}
            {isAdmin && (
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #f6f8fb 0%, #ffffff 100%)",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "2px solid #e5e7eb",
                  marginBottom: "24px",
                }}
              >
                <label
                  style={{
                    display: "block",
                    fontSize: "0.9rem",
                    fontWeight: "600",
                    color: "#64748b",
                    marginBottom: "12px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Assign Individual Course
                </label>
                <div
                  style={{ display: "flex", gap: "12px", alignItems: "center" }}
                >
                  <select
                    value={selectedCourseId}
                    onChange={(e) => setSelectedCourseId(e.target.value)}
                    className="form-control"
                    style={{
                      flex: "1",
                      padding: "12px 16px",
                      fontSize: "1rem",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      height: "48px",
                    }}
                  >
                    <option value="">Select course...</option>
                    {allCourses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title} (
                        {course.hasAssessment ? "Assessment" : "Mindfullness"})
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleAssignCourse}
                    style={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      padding: "12px 24px",
                      fontSize: "1rem",
                      fontWeight: "600",
                      cursor: "pointer",
                      height: "48px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Assign
                  </button>
                </div>
              </div>
            )}

            {/* Assigned Courses List */}
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid #e5e7eb",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ background: "#f8fafc" }}>
                  <tr>
                    <th
                      style={{
                        padding: "16px",
                        textAlign: "left",
                        borderBottom: "2px solid #e5e7eb",
                        fontWeight: "600",
                        color: "#1e293b",
                      }}
                    >
                      Course Title
                    </th>
                    {isAdmin && (
                      <th
                        style={{
                          padding: "16px",
                          textAlign: "right",
                          borderBottom: "2px solid #e5e7eb",
                          width: "100px",
                        }}
                      >
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {assignedCourses.length === 0 ? (
                    <tr>
                      <td
                        colSpan={isAdmin ? 2 : 1}
                        style={{
                          padding: "40px",
                          textAlign: "center",
                          color: "#64748b",
                        }}
                      >
                        No courses assigned to this group yet
                      </td>
                    </tr>
                  ) : (
                    assignedCourses.map((course) => (
                      <tr
                        key={course.id}
                        style={{ borderBottom: "1px solid #f1f5f9" }}
                      >
                        <td style={{ padding: "16px", color: "#1e293b" }}>
                          {course.title}
                        </td>
                        {isAdmin && (
                          <td style={{ padding: "16px", textAlign: "right" }}>
                            <button
                              data-target="#removeGroupCourseModal"
                              data-toggle="modal"
                              onClick={() => setItemToRemove(course)}
                              style={{
                                background:
                                  "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                padding: "6px 14px",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                                cursor: "pointer",
                              }}
                            >
                              Remove
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default GroupAssignments;
